import { ModuleInventory } from "./ModuleInventory.js";
import { useCallback, useEffect, useState } from "react";
import { COLLECTION_MILESTONES, UNITS, moduleArt } from "@zend/content";
import { pick, useT } from "../i18n/index.js";
import { useSession } from "../state/session.js";
import { useBalances } from "../state/balances.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { onWs } from "../api/ws.js";
import { BlueprintProduction, CraftQueue } from "./Crafting.js";
import { portraitUrl } from "../assets/manifest.js";
import { unitsApi } from "./api.js";
import { UnitCard } from "./UnitCard.js";
import { ModuleCompareSheet } from "./ModuleComparison.js";
import { useUnits } from "./state.js";
import { Stars, TierBadge, moduleName, tierColor, unitName } from "./shared.js";
import { GameIcon, ResourceIcon } from "../assets/gameIcons.js";
import { Icon } from "../ui/Icon.js";

export { RepairTab } from "./RepairTab.js";

/** Armory tab: craft queue with blueprint counts, enhance with cost preview, dismantle (R26). */
export function ArmoryTab() {
  const t = useT();
  const roster = useUnits((s) => s.roster);
  const run = useUnits((s) => s.run);
  const inventory = useSession((s) => s.inventory);
  const balances = useBalances((s) => s.balances);
  const [filter, setFilter] = useState<"blueprints" | "modules">("modules");
  const [compareModule, setCompareModule] = useState<string | null>(null);
  const [finishedIds, setFinishedIds] = useState<string[]>([]);
  const accountId = useSession(s => s.account?.id);
  const ready = useCallback((id: string) => setFinishedIds(ids => [...new Set([...ids, id])].slice(-3)), []);
  useEffect(() => { setFinishedIds([]); return onWs("craft.done", event => { const payload = event.payload as { moduleRowId?: unknown }; if (typeof payload?.moduleRowId === "string") ready(payload.moduleRowId); }); }, [accountId, ready]);
  if (!roster) return null;
  const nano = inventory.find((i) => i.itemId === "itm.nano")?.qty ?? 0;
  return (
    <div className="armory-depot" data-testid="armory-tab" data-production={filter === "blueprints"}>
      <div className="row" style={{ gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <span className="badge gold">
          {t("armory.queue")} {roster.craft.used} / {roster.craft.slots}
        </span>
        <span className="badge">{t("armory.maxTier", { tier: roster.craft.maxTier })}</span>
        <span className="badge"><GameIcon id="itm.nano" /> NANO {nano}</span>
        <span className="hint">{roster.craft.armoryLevel > 0 ? t("armory.level", { n: roster.craft.armoryLevel }) : t("armory.locked")}</span>
        <span className="grow" />
        <div className="tabs" style={{ margin: 0 }}>
          {(["modules", "blueprints"] as const).map((f) => (
            <button key={f} type="button" className={`tab ${filter === f ? "active" : ""}`} onClick={() => setFilter(f)} data-testid={`armory-filter-${f}`}>
              {t(`armory.${f}`)}
            </button>
          ))}
        </div>
      </div>
      {filter === "blueprints" || roster.craft.queue.length > 0 ? <CraftQueue craft={roster.craft} onReady={ready} /> : null}
      {finishedIds.map(id => roster.modules.find(m => m.id === id)).filter(m => !!m).map(m => <div key={m.id} className="craft-finished" data-testid={`craft-finished-${m.id}`}><GameIcon id={moduleArt(m.mfr, m.slot)} size={64} /><div><strong>{t("craft.done")}</strong><p>{moduleName(m.moduleId)}</p></div><Button variant="primary" onClick={() => setCompareModule(m.id)}>{t("craft.equip")}</Button></div>)}
      {filter === "blueprints" ? <BlueprintProduction craft={roster.craft} /> : (
        <ModuleInventory modules={roster.modules} units={roster.units} onCompare={setCompareModule} actions={m => <>
                  {m.nextEnhance ? (
                    <Button style={{ minHeight: 44 }} disabled={nano < m.nextEnhance.nano || (balances?.SCR ?? 0) < m.nextEnhance.SCR} onClick={() => void run(() => unitsApi.enhance(m.id), t("units.toast.enhance"))} data-testid={`enhance-${m.id}`}>
                      +1 · <ResourceIcon resource="SCR" label={t("res.SCR")} /> {formatCompact(m.nextEnhance.SCR)} · <GameIcon id="itm.nano" /> {m.nextEnhance.nano} NANO
                    </Button>
                  ) : (
                    <span className="badge gold">MAX</span>
                  )}
                  {!m.unitRowId ? (
                    <Button variant="danger" style={{ minHeight: 44 }} onClick={() => void run(() => unitsApi.dismantle(m.id), t("units.toast.dismantle"))} data-testid={`dismantle-${m.id}`} aria-label={t("armory.dismantle")}>
                      <Icon name="trash" />
                    </Button>
                  ) : null}
        </>} />
      )}
      {compareModule ? <ModuleCompareSheet moduleId={compareModule} onClose={() => setCompareModule(null)} /> : null}
    </div>
  );
}

/** Collection: all 45 silhouettes, owned vs. unowned, filters and milestone claims (R29). */
export function CollectionTab() {
  const t = useT();
  const roster = useUnits((s) => s.roster);
  const run = useUnits((s) => s.run);
  const select = useUnits((s) => s.select);
  const [tier, setTier] = useState<string>("all");
  if (!roster) return null;
  const owned = new Map(roster.units.map((u) => [u.unitId, u]));
  const list = UNITS.filter((u) => tier === "all" || u.tier === tier);
  return (
    <div data-testid="collection-tab">
      <div className="row" style={{ gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <span className="badge gold" data-testid="collection-count">
          {roster.collection.owned.length} / {UNITS.length}
        </span>
        <div className="tabs" style={{ margin: 0 }}>
          {["all", "S", "A", "B", "C", "D"].map((f) => (
            <button key={f} type="button" className={`tab ${tier === f ? "active" : ""}`} onClick={() => setTier(f)}>
              {f}
            </button>
          ))}
        </div>
      </div>
      <div className="milestones">
        {roster.collection.milestones.map((m) => {
          const def = COLLECTION_MILESTONES.find((x) => x.id === m.id)!;
          return (
            <div key={m.id} className={`milestone ${m.claimed ? "claimed" : m.reached ? "reached" : ""}`} data-testid={`milestone-${m.id}`}>
              <strong>{pick(def.name)}</strong>
              <div className="progress" style={{ margin: "4px 0" }}>
                <div className="progress-fill gold" style={{ width: `${Math.min(100, (m.progress / m.target) * 100)}%` }} />
              </div>
              <div className="row" style={{ gap: 6 }}>
                <span className="mono hint">
                  {m.progress} / {m.target}
                </span>
                <span className="grow" />
                {m.claimed ? <span className="badge ok"><Icon name="check" label={t("store.pass.claimed")} /></span> : (
                  <Button variant="gold" style={{ minHeight: 28, fontSize: 12 }} disabled={!m.reached} onClick={() => void run(() => unitsApi.claimMilestone(m.id), t("units.toast.milestone"))} data-testid={`claim-${m.id}`}>
                    <ResourceIcon resource="CREDITS" label={t("store.credits")} /> {def.reward.credits} +
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="unit-grid">
        {list.map((def) => {
          const u = owned.get(def.id);
          if (u) return <UnitCard key={def.id} unit={u} onClick={() => select(u.id)} />;
          return (
            <div key={def.id} className="unit-card silhouette" style={{ borderColor: `${tierColor(def.tier)}55` }} data-testid={`silhouette-${def.id}`}>
              <div className="unit-portrait">
                <img src={portraitUrl(def.id)} alt="" style={{ filter: "brightness(0) opacity(0.35)" }} draggable={false} />
                <TierBadge tier={def.tier} style={{ position: "absolute", top: 4, left: 4, opacity: 0.6 }} />
              </div>
              <div className="unit-card-body">
                <strong className="ellipsis dim">{unitName(def.id)}</strong>
                <Stars stars={0} size={9} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
